/**
 * Tabs component based on Radix UI Tabs primitive.
 *
 * A flexible tab navigation component with multiple visual styles and animated content transitions.
 *
 * @example
 * ```tsx
 * // Default tabs
 * <Tabs defaultValue="tab1">
 *   <TabsList>
 *     <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *     <TabsTrigger value="tab2">Tab 2</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="tab1">Content 1</TabsContent>
 *   <TabsContent value="tab2">Content 2</TabsContent>
 * </Tabs>
 *
 * // Underline variant
 * <Tabs defaultValue="tab1">
 *   <TabsList variant="underline">
 *     <TabsTrigger variant="underline" value="tab1">Tab 1</TabsTrigger>
 *   </TabsList>
 *   {/* ... *\/}
 * </Tabs>
 *
 * // Pills variant
 * <Tabs defaultValue="tab1">
 *   <TabsList variant="pills">
 *     <TabsTrigger variant="pills" value="tab1">Tab 1</TabsTrigger>
 *   </TabsList>
 *   {/* ... *\/}
 * </Tabs>
 *
 * // Pills with color variants
 * <Tabs defaultValue="tab1">
 *   <TabsList variant="pills">
 *     <TabsTrigger variant="pills-emerald" value="tab1">Emerald Tab</TabsTrigger>
 *     <TabsTrigger variant="pills-purple" value="tab2">Purple Tab</TabsTrigger>
 *     <TabsTrigger variant="pills-gold" value="tab3">Gold Tab</TabsTrigger>
 *     <TabsTrigger variant="pills-pink" value="tab4">Pink Tab</TabsTrigger>
 *   </TabsList>
 *   {/* ... *\/}
 * </Tabs>
 * ```
 */
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { motion } from 'motion/react';

import { cn } from '@/lib/utils';
import { tabContent } from '@/lib/animations';
import { useMotionProps } from '@/lib/cva-motion';

/**
 * CVA variants for TabsList component.
 *
 * Variants:
 * - default: Standard tabs with background and padding
 * - underline: Minimal style with bottom border
 * - pills: Modern rounded pill-style tabs
 */
const tabsListVariants = cva(
  'inline-flex items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
  {
    variants: {
      variant: {
        default: '',
        underline: 'bg-transparent p-0 border-b border-border',
        pills: 'bg-background p-1 gap-1',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

/**
 * CVA variants for TabsTrigger component.
 *
 * Variants:
 * - default: Standard trigger with background on active state
 * - underline: Underline-style with bottom border on active
 * - pills: Rounded pill-style with primary background on active
 *
 * Colors (for pills variant):
 * - default: Primary color
 * - emerald: Emerald green active indicator
 * - purple: Purple active indicator
 * - gold: Gold active indicator
 * - pink: Pink active indicator
 */
const tabsTriggerVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 body-small font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
        underline:
          'rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground',
        pills:
          'rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground',
        'pills-emerald':
          'rounded-md data-[state=active]:bg-emerald data-[state=active]:text-emerald-foreground',
        'pills-purple':
          'rounded-md data-[state=active]:bg-purple data-[state=active]:text-purple-foreground',
        'pills-gold':
          'rounded-md data-[state=active]:bg-gold data-[state=active]:text-gold-foreground',
        'pills-pink':
          'rounded-md data-[state=active]:bg-pink data-[state=active]:text-pink-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

/**
 * Props for the TabsList component.
 *
 * @property variant - Visual style variant (default, underline, pills)
 */
export interface TabsListProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

/**
 * Props for the TabsTrigger component.
 *
 * @property variant - Visual style variant (default, underline, pills)
 * @property value - Unique value identifying this tab
 */
export interface TabsTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

/**
 * Root component for Tabs. Controls the active tab state.
 *
 * @property defaultValue - Initial active tab value
 * @property value - Controlled active tab value
 * @property onValueChange - Callback when active tab changes
 */
const Tabs = TabsPrimitive.Root;

/**
 * Container for tab triggers. Provides visual grouping and styling.
 *
 * Features:
 * - Type-safe variants using CVA
 * - Responsive layout
 * - Accessible via Radix UI
 */
const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, variant, ...props }, ref) => (
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  ),
);
TabsList.displayName = TabsPrimitive.List.displayName;

/**
 * Individual tab trigger button.
 *
 * Features:
 * - Type-safe variants using CVA
 * - Keyboard navigation support
 * - Accessible via Radix UI
 * - Active state styling
 */
const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant }), className)}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

/**
 * Motion-enhanced TabsTrigger component with automatic animations.
 *
 * Provides the same API as TabsTrigger but with built-in motion animations:
 * - Subtle scale on hover
 * - Smooth focus transitions
 * - Enhanced visual feedback
 *
 * @example
 * ```tsx
 * <Tabs defaultValue="tab1">
 *   <TabsList>
 *     <MotionTabsTrigger variant="pills-emerald" value="tab1">
 *       Tab 1
 *     </MotionTabsTrigger>
 *   </TabsList>
 * </Tabs>
 * ```
 */
const MotionTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, children, ...props }, ref) => {
  const motionProps = useMotionProps({
    hover: 'scale',
    tap: 'scale',
    focus: 'glow',
    initial: 'none',
  });

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant }), className)}
      asChild
      {...props}
    >
      <motion.button
        {...(motionProps as any)}
        className={cn(tabsTriggerVariants({ variant }), className)}
      >
        {children}
      </motion.button>
    </TabsPrimitive.Trigger>
  );
});
MotionTabsTrigger.displayName = 'MotionTabsTrigger';

/**
 * Content panel for a tab.
 *
 * Features:
 * - Animated transitions when switching tabs
 * - Only visible when corresponding tab is active
 * - Smooth horizontal slide animation
 * - Accessible via Radix UI
 *
 * @property value - Must match the value of the corresponding TabsTrigger
 */
const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, children, asChild: _ignoredAsChild, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  >
    <motion.div
      variants={tabContent}
      initial='hidden'
      animate='visible'
      exit='exit'
      layout
      className='w-full'
    >
      {children}
    </motion.div>
  </TabsPrimitive.Content>
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export {
  MotionTabsTrigger,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  tabsListVariants,
  tabsTriggerVariants,
};
